import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { RxTemplateService } from "../../api/rxtemplate";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";

interface FormData {
  prescriptionName: string;
  medication: string[];
  notes: string;
  description: string;
  duration: string;
  dosage: string;
}

interface FormErrors {
  prescriptionName?: string;
  medication?: string;
  notes?: string;
  duration?: string;
}

const RxTemplateEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [medicationInput, setMedicationInput] = useState("");

  const [formData, setFormData] = useState<FormData>({
    prescriptionName: "",
    medication: [],
    notes: "",
    description: "",
    duration: "",
    dosage: "",
  });

  const [durationNumber, setDurationNumber] = useState("");
  const [durationUnit, setDurationUnit] = useState("");
  const [dosageNumber, setDosageNumber] = useState("");
  const [dosageUnit, setDosageUnit] = useState("");

  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (id) {
      fetchRxTemplate();
    }
  }, [id]);

  const fetchRxTemplate = async () => {
    setInitialLoading(true);
    try {
      const response = await RxTemplateService.getRxTemplateById(id!);
      const data = response.data?.data || response.data || {};

      const duration = data.duration || "";
      const dosage = data.dosage || "";

      // Parse duration (e.g., "7 days" -> number: "7", unit: "days")
      if (duration) {
        const durationMatch = duration.match(/^(\d+)\s+(days?|weeks?|months?)$/i);
        if (durationMatch) {
          setDurationNumber(durationMatch[1]);
          const unit = durationMatch[2].toLowerCase();
          // Normalize to plural form for consistency
          if (unit === "day") setDurationUnit("days");
          else if (unit === "week") setDurationUnit("weeks");
          else if (unit === "month") setDurationUnit("months");
          else setDurationUnit(unit);
        }
      }

      // Parse dosage (e.g., "500 mg" -> number: "500", unit: "mg")
      if (dosage) {
        const dosageMatch = dosage.match(/^(\d+)\s+(mg|g|ml|units?)$/i);
        if (dosageMatch) {
          setDosageNumber(dosageMatch[1]);
          const unit = dosageMatch[2].toLowerCase();
          // Normalize units
          if (unit === "unit") setDosageUnit("units");
          else setDosageUnit(unit);
        }
      }

      setFormData({
        prescriptionName: data.prescriptionName || "",
        medication: data.medication || [],
        notes: data.notes || "",
        description: data.description || "",
        duration: duration,
        dosage: dosage,
      });
    } catch (error) {
      console.error("Failed to fetch RxTemplate:", error);
      toast.error("Failed to fetch RxTemplate");
      navigate("/rxtemplates");
    } finally {
      setInitialLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.prescriptionName.trim()) {
      newErrors.prescriptionName = "Prescription name is required";
    }

    if (formData.medication.length === 0) {
      newErrors.medication = "At least one medication is required";
    }

    if (!formData.notes.trim()) {
      newErrors.notes = "Notes are required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleAddMedication = () => {
    if (
      medicationInput.trim() &&
      !formData.medication.includes(medicationInput.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        medication: [...prev.medication, medicationInput.trim()],
      }));
      setMedicationInput("");

      // Clear medication error
      if (errors.medication) {
        setErrors((prev) => ({
          ...prev,
          medication: undefined,
        }));
      }
    }
  };

  const handleRemoveMedication = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      medication: prev.medication.filter((_, i) => i !== index),
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddMedication();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Combine duration and dosage
    const combinedDuration = durationNumber && durationUnit
      ? `${durationNumber} ${durationUnit}`
      : "";
    const combinedDosage = dosageNumber && dosageUnit
      ? `${dosageNumber} ${dosageUnit}`
      : "";

    // Validate duration - check if 0
    const newErrors: FormErrors = {};
    if (durationNumber && durationUnit) {
      const num = parseInt(durationNumber, 10);
      if (num === 0) {
        newErrors.duration = "Duration cannot be 0";
        setErrors(newErrors);
        return;
      }
    }

    // Validate other fields
    if (!formData.prescriptionName.trim()) {
      newErrors.prescriptionName = "Prescription name is required";
    }
    if (formData.medication.length === 0) {
      newErrors.medication = "At least one medication is required";
    }
    if (!formData.notes.trim()) {
      newErrors.notes = "Notes are required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        prescriptionName: formData.prescriptionName.trim(),
        medication: formData.medication,
        notes: formData.notes.trim(),
        description: formData.description.trim() || null,
        duration: combinedDuration || null,
        dosage: combinedDosage || null,
      };

      await RxTemplateService.updateRxTemplate(id!, payload);
      toast.success("RxTemplate updated successfully!");
      navigate("/rxtemplates");
    } catch (error: any) {
      console.error("Failed to update RxTemplate:", error);
      toast.error(
        error.response?.data?.message || "Failed to update RxTemplate"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/rxtemplates");
  };

  if (initialLoading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}

      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        {/* Page Content */}
        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  Edit RxTemplate
                </h2>

                <button
                  className="px-4 py-1 mb-2 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition flex items-center gap-2"
                  onClick={() => window.history.back()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M10 19l-7-7 7-7" />
                    <path d="M3 12h18" />
                  </svg>
                  Back
                </button>
              </div>
              <form
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                {/* Prescription Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Prescription Name *
                  </label>
                  <input
                    type="text"
                    name="prescriptionName"
                    value={formData.prescriptionName}
                    onChange={handleInputChange}
                    className={`w-full p-2 border rounded-xl transition-all duration-200 ${errors.prescriptionName
                      ? "border-red-500"
                      : "border-gray-200"
                      }`}
                    placeholder="Enter prescription name"
                  />
                  {errors.prescriptionName && (
                    <p className="text-red-500 text-sm">
                      {errors.prescriptionName}
                    </p>
                  )}
                </div>

                {/* Duration and Dosage */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Duration
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        value={durationNumber}
                        onChange={(e) => setDurationNumber(e.target.value)}
                        className="w-1/2 p-3 border border-gray-200 rounded-xl transition-all duration-200"
                        placeholder="Number"
                      />
                      <select
                        value={durationUnit}
                        onChange={(e) => setDurationUnit(e.target.value)}
                        className="w-1/2 p-3 border border-gray-200 rounded-xl transition-all duration-200"
                      >
                        <option value="" disabled>Select unit</option>
                        <option value="days">days</option>
                        <option value="weeks">weeks</option>
                        <option value="months">months</option>
                      </select>
                    </div>
                    {errors.duration && (
                      <p className="text-red-500 text-sm">{errors.duration}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Dosage
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        value={dosageNumber}
                        onChange={(e) => setDosageNumber(e.target.value)}
                        className="w-1/2 p-3 border border-gray-200 rounded-xl transition-all duration-200"
                        placeholder="Number"
                      />
                      <select
                        value={dosageUnit}
                        onChange={(e) => setDosageUnit(e.target.value)}
                        className="w-1/2 p-3 border border-gray-200 rounded-xl transition-all duration-200"
                      >
                        <option value="" disabled>Select unit</option>
                        <option value="mg">mg</option>
                        <option value="g">g</option>
                        <option value="ml">ml</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Notes *
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className={`w-full p-3 border rounded-xl transition-all duration-200 resize-none ${errors.notes ? "border-red-500" : "border-gray-200"
                      }`}
                    placeholder="Enter notes"
                  />
                  {errors.notes && (
                    <p className="text-red-500 text-sm">{errors.notes}</p>
                  )}
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full p-3 border border-gray-200 rounded-xl transition-all duration-200 resize-none"
                    placeholder="Enter description (optional)"
                  />
                </div>

                {/* Medication */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Medication *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={medicationInput}
                      onChange={(e) => setMedicationInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="flex-1 p-2 border border-gray-200 rounded-xl transition-all duration-200"
                      placeholder="Enter medication name"
                    />
                    <button
                      type="button"
                      onClick={handleAddMedication}
                      className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      Add
                    </button>
                  </div>

                  {/* Medication List */}
                  {formData.medication.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {formData.medication.map((med, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                        >
                          <span className="text-sm text-gray-700">{med}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveMedication(index)}
                            className="text-red-500 hover:text-red-700 transition-colors duration-200"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {errors.medication && (
                    <p className="text-red-500 text-sm">{errors.medication}</p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="md:col-span-2 flex justify-end gap-3 mt-2">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="flex flex-col sm:flex-row justify-end gap-4 pt-6"
                  >
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={handleCancel}
                      className="px-4 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      Update Template
                    </motion.button>
                  </motion.div>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default RxTemplateEdit;
